// UserManagementTab.tsx — Admin Users tab (#232)
// Privacy-by-design: search-only access, reason required on every operation.
// No browseable user table. All write ops require super_admin role.

import React, { useState } from 'react';
import {
    Box, Typography, TextField, Button, Card, CardContent,
    Alert, Chip, Divider, CircularProgress, Dialog, DialogTitle,
    DialogContent, DialogActions, Select, MenuItem, FormControl,
    InputLabel, Table, TableBody, TableRow, TableCell,
} from '@mui/material';
import { Search, Person, Block, CheckCircle, Edit } from '@mui/icons-material';
import { api } from '../../utils/api';

interface UserResult {
    userId: string;
    email: string;
    displayName: string;
    username: string;
    role: string;
    tier: string | null;
    isActive: boolean;
    emailVerified: boolean;
    createdAt: string;
    lastLogin: string | null;
}

interface UserDetail extends UserResult {
    bio: string;
    avatarUrl: string;
    deletedAt: string | null;
    activity: {
        tripCount: number;
        photoCount: number;
        friendCount: number;
    };
}

const TEAL = '#2D6E6A';

const roleBadgeColor = (role: string) => {
    if (role === 'super_admin') return 'error';
    if (role === 'admin') return 'warning';
    return 'default';
};

const UserManagementTab: React.FC = () => {
    // ── Search state ──────────────────────────────────────────────────────────
    const [searchQuery, setSearchQuery] = useState('');
    const [searchReason, setSearchReason] = useState('');
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchError, setSearchError] = useState('');
    const [searchResults, setSearchResults] = useState<UserResult[]>([]);
    const [hasSearched, setHasSearched] = useState(false);

    // ── All users list state ──────────────────────────────────────────────────
    const [allUsers, setAllUsers] = useState<UserResult[]>([]);
    const [allUsersLoading, setAllUsersLoading] = useState(false);
    const [allUsersError, setAllUsersError] = useState('');
    const [allUsersPage, setAllUsersPage] = useState(1);
    const [allUsersTotalPages, setAllUsersTotalPages] = useState(1);
    const [allUsersTotal, setAllUsersTotal] = useState(0);
    const [showAllUsers, setShowAllUsers] = useState(false);

    // ── Selected user state ───────────────────────────────────────────────────
    const [selectedUser, setSelectedUser] = useState<UserDetail | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState('');

    // ── Action dialogs ────────────────────────────────────────────────────────
    const [actionDialog, setActionDialog] = useState<'suspend' | 'activate' | 'edit' | null>(null);
    const [actionReason, setActionReason] = useState('');
    const [actionLoading, setActionLoading] = useState(false);
    const [actionError, setActionError] = useState('');
    const [actionSuccess, setActionSuccess] = useState('');
    const [editRole, setEditRole] = useState('');

    // ── Search ────────────────────────────────────────────────────────────────
    const handleSearch = async () => {
        if (!searchQuery.trim() || searchQuery.trim().length < 2) {
            setSearchError('Enter at least 2 characters to search');
            return;
        }
        if (!searchReason.trim() || searchReason.trim().length < 10) {
            setSearchError('Reason must be at least 10 characters');
            return;
        }
        setSearchError('');
        setSearchLoading(true);
        setSearchResults([]);
        setSelectedUser(null);
        setHasSearched(true);

        try {
            const res = await api.post('/admin/users/search', {
                query: searchQuery.trim(),
                reason: searchReason.trim(),
            });
            const data = await res.json();
            if (!res.ok) {
                setSearchError(data.error || 'Search failed');
                return;
            }
            setSearchResults(data.users || []);
        } catch {
            setSearchError('Search failed. Please try again.');
        } finally {
            setSearchLoading(false);
        }
    };

    // ── Load all users ────────────────────────────────────────────────────────
    const loadAllUsers = async (page: number = 1) => {
        setAllUsersLoading(true);
        setAllUsersError('');
        try {
            const res = await api.get(`/admin/users?page=${page}`);
            const data = await res.json();
            if (!res.ok) {
                setAllUsersError(data.error || 'Failed to load users');
                return;
            }
            setAllUsers(data.users || []);
            setAllUsersPage(data.pagination.page);
            setAllUsersTotalPages(data.pagination.totalPages);
            setAllUsersTotal(data.pagination.total);
            setShowAllUsers(true);
        } catch {
            setAllUsersError('Failed to load users. Please try again.');
        } finally {
            setAllUsersLoading(false);
        }
    };

    // ── Load user detail ──────────────────────────────────────────────────────
    const handleSelectUser = async (user: UserResult) => {
        setDetailLoading(true);
        setDetailError('');
        setSelectedUser(null);
        setActionSuccess('');

        try {
            const res = await api.get(
                `/admin/users/${user.userId}?reason=Admin+dashboard+user+review`
            );
            const data = await res.json();
            if (!res.ok) {
                setDetailError(data.error || 'Failed to load user');
                return;
            }
            setSelectedUser({ ...data.user, activity: data.activity });
            setEditRole(data.user.role || 'user');
        } catch {
            setDetailError('Failed to load user details.');
        } finally {
            setDetailLoading(false);
        }
    };

    // ── Actions ───────────────────────────────────────────────────────────────
    const handleAction = async () => {
        if (!selectedUser || !actionDialog) return;
        if (!actionReason.trim() || actionReason.trim().length < 10) {
            setActionError('Reason must be at least 10 characters');
            return;
        }
        setActionLoading(true);
        setActionError('');

        try {
            let res: Response | undefined;
            if (actionDialog === 'suspend') {
                res = await api.post(`/admin/users/${selectedUser.userId}/suspend`, {
                    reason: actionReason.trim(),
                });
            } else if (actionDialog === 'activate') {
                res = await api.post(`/admin/users/${selectedUser.userId}/activate`, {
                    reason: actionReason.trim(),
                });
            } else if (actionDialog === 'edit') {
                res = await api.put(`/admin/users/${selectedUser.userId}`, {
                    role: editRole,
                    reason: actionReason.trim(),
                });
            }

            const data = await res!.json();
            if (!res!.ok) {
                setActionError(data.error || 'Action failed');
                return;
            }

            setActionSuccess(`${actionDialog} completed successfully`);
            setActionDialog(null);
            setActionReason('');
            await handleSelectUser(selectedUser);
        } catch {
            setActionError('Action failed. Please try again.');
        } finally {
            setActionLoading(false);
        }
    };

    const closeDialog = () => {
        setActionDialog(null);
        setActionReason('');
        setActionError('');
    };

    return (
        <Box>
            <Typography variant="h5" fontWeight={600} gutterBottom>
                User Management
            </Typography>
            <Alert severity="info" sx={{ mb: 3 }}>
                Search-only access. A reason is required for every lookup. All operations are audit-logged.
            </Alert>

            {/* ── Search panel ──────────────────────────────────────────────── */}
            <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                            Search Users
                        </Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => showAllUsers ? setShowAllUsers(false) : loadAllUsers(1)}
                            disabled={allUsersLoading}
                            sx={{ borderColor: TEAL, color: TEAL }}
                        >
                            {allUsersLoading ? <CircularProgress size={14} /> : showAllUsers ? 'Hide All Users' : 'Show All Users'}
                        </Button>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                        <TextField
                            label="Email, display name, or username"
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleSearch()}
                            size="small"
                            sx={{ flex: 2, minWidth: 220 }}
                        />
                        <TextField
                            label="Reason for lookup (min 10 chars)"
                            value={searchReason}
                            onChange={e => setSearchReason(e.target.value)}
                            size="small"
                            sx={{ flex: 3, minWidth: 260 }}
                        />
                        <Button
                            variant="contained"
                            onClick={handleSearch}
                            disabled={searchLoading}
                            startIcon={searchLoading ? <CircularProgress size={16} color="inherit" /> : <Search />}
                            sx={{ bgcolor: TEAL, '&:hover': { bgcolor: '#009688' }, whiteSpace: 'nowrap' }}
                        >
                            Search
                        </Button>
                    </Box>
                    {searchError && <Alert severity="error" sx={{ mt: 1 }}>{searchError}</Alert>}
                </CardContent>
            </Card>

            {/* ── All users table ───────────────────────────────────────────── */}
            {showAllUsers && (
                <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                                All Users ({allUsersTotal} total)
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <Button size="small" disabled={allUsersPage <= 1} onClick={() => loadAllUsers(allUsersPage - 1)}>Prev</Button>
                                <Typography variant="body2">Page {allUsersPage} of {allUsersTotalPages}</Typography>
                                <Button size="small" disabled={allUsersPage >= allUsersTotalPages} onClick={() => loadAllUsers(allUsersPage + 1)}>Next</Button>
                            </Box>
                        </Box>
                        {allUsersError && <Alert severity="error" sx={{ mb: 2 }}>{allUsersError}</Alert>}
                        <Table size="small">
                            <TableBody>
                                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                    <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Display Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Joined</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Last Login</TableCell>
                                </TableRow>
                                {allUsers.map(user => (
                                    <TableRow
                                        key={user.userId}
                                        onClick={() => handleSelectUser(user)}
                                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f0fafa' } }}
                                    >
                                        <TableCell>{user.email}</TableCell>
                                        <TableCell>{user.displayName || '—'}</TableCell>
                                        <TableCell><Chip label={user.role} size="small" color={roleBadgeColor(user.role)} /></TableCell>
                                        <TableCell><Chip label={user.isActive ? 'Active' : 'Suspended'} size="small" color={user.isActive ? 'success' : 'error'} /></TableCell>
                                        <TableCell>{new Date(user.createdAt).toLocaleDateString()}</TableCell>
                                        <TableCell>{user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            )}

            {/* ── Search results ────────────────────────────────────────────── */}
            {hasSearched && !searchLoading && (
                <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
                    <CardContent>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                            {searchResults.length === 0
                                ? 'No users found'
                                : `${searchResults.length} result${searchResults.length > 1 ? 's' : ''} (max 10)`}
                        </Typography>
                        {searchResults.map(user => (
                            <Box
                                key={user.userId}
                                onClick={() => handleSelectUser(user)}
                                sx={{
                                    p: 2, mb: 1, borderRadius: 2, cursor: 'pointer',
                                    border: '1px solid',
                                    borderColor: selectedUser?.userId === user.userId ? TEAL : 'divider',
                                    bgcolor: selectedUser?.userId === user.userId ? '#f0fafa' : 'transparent',
                                    '&:hover': { bgcolor: '#f0fafa' },
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <Person fontSize="small" sx={{ color: TEAL }} />
                                    <Typography variant="body2" fontWeight={600}>{user.displayName}</Typography>
                                    <Typography variant="body2" color="text.secondary">({user.email})</Typography>
                                    <Chip label={user.role} size="small" color={roleBadgeColor(user.role)} />
                                    {!user.isActive && <Chip label="Suspended" size="small" color="error" />}
                                    {!user.emailVerified && <Chip label="Unverified" size="small" color="warning" />}
                                </Box>
                            </Box>
                        ))}
                    </CardContent>
                </Card>
            )}

            {/* ── User detail ───────────────────────────────────────────────── */}
            {detailLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress sx={{ color: TEAL }} />
                </Box>
            )}
            {detailError && <Alert severity="error" sx={{ mb: 2 }}>{detailError}</Alert>}
            {actionSuccess && <Alert severity="success" sx={{ mb: 2 }}>{actionSuccess}</Alert>}

            {selectedUser && !detailLoading && (
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                            <Box>
                                <Typography variant="h6" fontWeight={600}>{selectedUser.displayName}</Typography>
                                <Typography variant="body2" color="text.secondary">{selectedUser.email}</Typography>
                                <Typography variant="caption" color="text.secondary">@{selectedUser.username}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Chip label={selectedUser.role} color={roleBadgeColor(selectedUser.role)} />
                                <Chip
                                    label={selectedUser.isActive ? 'Active' : 'Suspended'}
                                    color={selectedUser.isActive ? 'success' : 'error'}
                                />
                            </Box>
                        </Box>

                        <Divider sx={{ mb: 2 }} />

                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Activity</Typography>
                        <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
                            <Box><Typography variant="h6" fontWeight={700}>{selectedUser.activity?.tripCount ?? 0}</Typography><Typography variant="caption">Trips</Typography></Box>
                            <Box><Typography variant="h6" fontWeight={700}>{selectedUser.activity?.photoCount ?? 0}</Typography><Typography variant="caption">Photos</Typography></Box>
                            <Box><Typography variant="h6" fontWeight={700}>{selectedUser.activity?.friendCount ?? 0}</Typography><Typography variant="caption">Friends</Typography></Box>
                        </Box>

                        <Divider sx={{ mb: 2 }} />

                        <Table size="small" sx={{ mb: 2 }}>
                            <TableBody>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600, width: 160 }}>Created</TableCell>
                                    <TableCell>{new Date(selectedUser.createdAt).toLocaleDateString()}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Last login</TableCell>
                                    <TableCell>{selectedUser.lastLogin ? new Date(selectedUser.lastLogin).toLocaleDateString() : 'Never'}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Email verified</TableCell>
                                    <TableCell>{selectedUser.emailVerified ? 'Yes' : 'No'}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Tier</TableCell>
                                    <TableCell>{selectedUser.tier || 'default'}</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>

                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Button
                                variant="outlined"
                                startIcon={<Edit />}
                                onClick={() => { setActionDialog('edit'); setEditRole(selectedUser.role); }}
                                size="small"
                            >
                                Edit Role
                            </Button>
                            {selectedUser.isActive ? (
                                <Button
                                    variant="outlined"
                                    color="error"
                                    startIcon={<Block />}
                                    onClick={() => setActionDialog('suspend')}
                                    size="small"
                                >
                                    Suspend
                                </Button>
                            ) : (
                                <Button
                                    variant="outlined"
                                    color="success"
                                    startIcon={<CheckCircle />}
                                    onClick={() => setActionDialog('activate')}
                                    size="small"
                                >
                                    Activate
                                </Button>
                            )}
                        </Box>
                    </CardContent>
                </Card>
            )}

            {/* ── Action dialogs ────────────────────────────────────────────── */}
            <Dialog open={actionDialog !== null} onClose={closeDialog} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                    {actionDialog} Account
                </DialogTitle>
                <DialogContent>
                    {actionError && <Alert severity="error" sx={{ mb: 2 }}>{actionError}</Alert>}

                    {actionDialog === 'edit' && (
                        <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
                            <InputLabel>Role</InputLabel>
                            <Select value={editRole} onChange={e => setEditRole(e.target.value)} label="Role">
                                <MenuItem value="user">User</MenuItem>
                                <MenuItem value="admin">Admin</MenuItem>
                                <MenuItem value="super_admin">Super Admin</MenuItem>
                            </Select>
                        </FormControl>
                    )}

                    {actionDialog === 'suspend' && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            This will immediately prevent the user from logging in.
                        </Alert>
                    )}

                    <TextField
                        fullWidth
                        label="Reason (min 10 chars) — audit logged"
                        value={actionReason}
                        onChange={e => setActionReason(e.target.value)}
                        multiline
                        rows={2}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={closeDialog}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleAction}
                        disabled={actionLoading || actionReason.trim().length < 10}
                        color={actionDialog === 'suspend' ? 'error' : 'primary'}
                        sx={actionDialog !== 'suspend' ? { bgcolor: TEAL, '&:hover': { bgcolor: '#009688' } } : {}}
                    >
                        {actionLoading ? <CircularProgress size={20} color="inherit" /> : 'Confirm'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default UserManagementTab;
